import { Box, Divider, InputBase, MenuItem, Select, styled } from "@mui/material";
import { OperatorValueTypeEnum } from "@sinch/core/entities/serverEnums";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { MdsExpandMore } from "@sinch/icons";
import { mapToOptions, Option } from "@sinch/utils/option";
import { RequirementsData } from "@sinch/views/ShiftTimeline/StaffFilter/queries.graphql";
import { find, head, isNil, map, prop, propEq } from "ramda";
import { ensureArray, isNotNilOrEmpty, isTruthy } from "ramda-adjunct";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon, useMuiController } from "@sinch/components";
import { RequirementsDataQuery } from "@sinch/components/application/Requirements/queries.types";
import { FullHeightAdornment } from "@sinch/components/form/FullHeightAdornment/FullHeightAdornment";
import { NumberInputBase } from "@sinch/components/form/NumberInputBase/NumberInputBase";
import { SelectInput, SelectInputProps } from "@sinch/components/form/SelectInput/SelectInput";
import { useFocusOnMount } from "@sinch/components/providers/AutofocusProvider/FocusProvider";

type RequirementOperatorSelectProps = {
  requirementId: string | number | undefined;
  subject: string | null;
  operatorFieldProps: ReturnType<typeof useMuiController>["field"] & Omit<SelectInputProps, "ref">;
  valueFieldProps: ReturnType<typeof useMuiController>["field"];
};

export const RequirementOperatorInput = (props: RequirementOperatorSelectProps) => {
  const { t } = useTranslation();
  const [invalidValue, setInvalidValue] = useState(false);
  const { data, loading } = useQueryHandler<RequirementsDataQuery>(RequirementsData);
  const invalidOperator = props?.operatorFieldProps?.error ?? props?.valueFieldProps?.error ?? false;

  const subject = data?.requirements
    ?.find((r) => r.id === props.requirementId)
    ?.subjects?.find((s) => s.subject === props.subject);

  const operatorsOptions: Option[] = useMemo(
    () => (subject?.operators ? mapToOptions("value", "label")(subject?.operators) : []),
    [subject?.operators]
  );

  const hasValue = subject?.operators
    ? prop("hasValue", find(propEq("value", props.operatorFieldProps.value), subject?.operators))
    : null;

  useEffect(() => {
    if (!loading) {
      props.operatorFieldProps.onChange?.(
        props.operatorFieldProps.value ? props.operatorFieldProps.value : (head(operatorsOptions)?.value ?? null)
      );
    }
  }, [loading, operatorsOptions, props.operatorFieldProps.value]);

  return (
    <CombinedSelectInput
      endAdornment={
        <FullHeightAdornment
          position="end"
          sx={{ backgroundColor: ({ palette }) => (hasValue ? undefined : palette.grey[200]) }}
        >
          <Divider orientation="vertical" sx={{ mr: 1 }} />
          <RequirementValueInput
            inputProps={props.valueFieldProps}
            invalid={props.operatorFieldProps.error || props.valueFieldProps.error}
            onValidate={setInvalidValue}
            selectedOperator={props.operatorFieldProps.value}
            subject={subject}
          />
        </FullHeightAdornment>
      }
      fullWidth
      label={t("Requirement.condition")}
      MenuProps={{ disablePortal: true }}
      size="small"
      {...props.operatorFieldProps}
      disabled={isNil(subject)}
      error={invalidOperator || invalidValue}
      errorMessage={invalidOperator || invalidValue ? [t("required")] : undefined}
      onChange={(e) => {
        props.operatorFieldProps.onChange(e);
      }}
      options={operatorsOptions}
      value={props.operatorFieldProps.value ?? null}
    />
  );
};

const CombinedSelectInput = styled(SelectInput)<SelectInputProps>(() => ({
  paddingRight: 0,
  "&>.MuiSelect-icon": { position: "relative", right: 0 },
  "&>.MuiSelect-select.MuiOutlinedInput-input.MuiOutlinedInput-input": { width: "50%", paddingRight: 0 },
  "&>.MuiInputAdornment-root": {
    width: "100%",
    maxWidth: "50%",
    "& .MuiInputBase-root": { width: "calc(100% - 8px)" },
  },
}));

type SubjectFields = RequirementsDataQuery["requirements"][0]["subjects"][0];

const RequirementValueInput: FC<{
  subject?: SubjectFields;
  onValidate: (invalid: boolean) => void;
  selectedOperator: string | null;
  inputProps: ReturnType<typeof useMuiController>["field"];
  invalid?: boolean;
}> = ({ subject, onValidate, selectedOperator, inputProps, invalid }) => {
  const hasValue = subject?.operators
    ? prop("hasValue", find(propEq("value", selectedOperator), subject?.operators))
    : null;

  const isMultiple = subject?.operators
    ? prop("isMultiple", find(propEq("value", selectedOperator), subject?.operators))
    : null;

  const ref = useFocusOnMount(inputProps.inputRef);

  useEffect(() => onValidate(invalid ?? false), [invalid, onValidate]);

  useEffect(() => {
    if (!hasValue && inputProps.value !== null) {
      inputProps.onChange(null);
    }
  }, [inputProps, hasValue]);

  switch (subject?.valueType) {
    case OperatorValueTypeEnum.Number:
      return (
        <NumberInputBase
          {...inputProps}
          disabled={!hasValue}
          inputRef={ref}
          size="small"
          value={inputProps.value ?? ""}
        />
      );
    case OperatorValueTypeEnum.Boolean:
      return <InputBase {...inputProps} disabled inputRef={ref} size="small" value={inputProps.value ?? ""} />;
    case OperatorValueTypeEnum.List:
      return (
        <Select
          disabled={!hasValue}
          IconComponent={(iconProps) => <MdsIcon {...iconProps} fontSize="small" icon={MdsExpandMore} />}
          input={<InputBase />}
          MenuProps={{ disablePortal: true }}
          multiple={isMultiple ?? false}
          renderValue={(selected) => {
            // This part is only for fixing the issue of text overflow in select input
            const selectedOption = map(
              (item) =>
                find<{ value: string; label: string }>(propEq("value", item), subject?.possibleValues || [])?.label,
              ensureArray(selected)
            ).filter(isTruthy);
            return (
              <Box
                sx={{
                  textOverflow: "inherit",
                  whiteSpace: "inherit",
                  overflow: "inherit",
                }}
              >
                {selectedOption.join(", ")}
              </Box>
            );
          }}
          size="small"
          {...inputProps}
          inputRef={ref}
          value={getListValues(inputProps.value, isMultiple)}
        >
          {map(
            (option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ),
            subject?.possibleValues || []
          )}
        </Select>
      );
    default:
      return (
        <InputBase {...inputProps} disabled={!hasValue} inputRef={ref} size="small" value={inputProps.value ?? ""} />
      );
  }
};

function getListValues(fieldValue: any | any[], isMultiple?: boolean | null) {
  const arrayValue = isMultiple ? ensureArray(fieldValue) : fieldValue;
  const emptyValue = isMultiple ? [] : null;
  return isNotNilOrEmpty(fieldValue) ? arrayValue : emptyValue;
}
